import React, { forwardRef, useCallback, useEffect, useState } from 'react';

import { Accordion, Flex, AccordionItem, AccordionButton, AccordionPanel } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { openTitleAndMetricPanel } from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSelector, useDispatch } from 'react-redux';
import { kRDrawerSelector } from 'selectors/kRDrawer';
import { kRDrawerSecondaryPanelSelector } from 'selectors/krSecondaryPanel';
import { selectMSTeamsContext } from 'selectors/msTeamsSelector';
import { okrPanelKebabMenuActionSelector } from 'selectors/okrPanelKebabMenuActionsSelector';
import { checkGoalGenAIEnabled } from 'utils/helpers/featureFlags';

import {
  Clone,
  Overflow,
  Realign,
  Share,
  MeshLogo,
  ChevronDown,
  Archive,
  ObjectiveIcon,
  MeshAiRegularWithoutPadding,
} from 'assets/assetsV1/iconComponents';
import { KeyResult } from 'assets/assetsV1/iconComponents/KeyResult';

import { OKR_TYPES } from 'constants/ProjectConstants';
import { isMobileView } from 'constants/isMobileView';
import {
  sendClickedOnCreateKRevent,
  sendClickedOnCreateObjectiveEvent,
  sendCreatedObjectiveEvent,
  sendCreatedKREvent,
  sendOpenedKRPanelEvent,
  sendOpenedObjectivePanelEvent,
  sendPageViewEvent,
  sendClickedOnKebabMenuEvent,
  sendClickedRealignedEvent,
  sendShareKREvent,
  sendShareObjectiveEvent,
  sendMeshMavenClickedEvent,
  sendAIButtonClickedEvent,
  sendClickedOnHelpMeWriteEvent,
} from 'constants/mixpanelHelper';
import { GOALS_EVENT_SOURCES } from 'constants/trackingEvents';

import { Button } from 'uiToolkitV1/Button';
import { Menu, MenuButton, MenuItem, MenuList } from 'uiToolkitV1/MenuV2';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { useOKRAccessControl } from '../AccessControl/useOKRAccessControl';
import { useChangeTimelineRealignModalLogic } from '../SecondaryPanel/RealignPanel/useChangeTimelineRealignModalLogic';
import { openCreateOKRPanel, SECONDARY_PANEL_TYPES } from '../krDrawerSecondaryPanelSlice';
import { ACTIONS_TO_PERFORM, markActionAsDone } from '../okrPanelKebabMenuActionsSlice';
import { OKRCloneModal } from './OKRCloneModal';
import { OKRDeletionModal } from './OKRDeletionModal';
import { RealignModal } from './RealignModal';
import { useOKRCloneModalLogic } from './useOKRCloneModalLogic';
import { useOKRDeletionModalLogic } from './useOKRDeletionModalLogic';
import { useShareOKRLogic } from './useShareOKRLogic';

const menuItems = [
  {
    icon: MeshAiRegularWithoutPadding,
    value: 'MESH_AI',
    label: 'Help me write',
    order: 1,
  },
  {
    icon: Realign,
    value: 'REALIGN',
    label: 'Re-align',
    order: 4,
  },
  {
    icon: Share,
    value: 'SHARE',
    label: 'Share',
    order: 5,
  },
  {
    icon: Clone,
    value: 'CLONE',
    label: 'Make a Clone',
    order: 6,
  },
  {
    icon: Archive,
    value: 'DELETE',
    label: 'Archive',
    order: 7,
  },
];

const usePerformDefaultAction = ({ handleDelete, handleRealign, handleClone, isDataAvailable }) => {
  const dispatch = useDispatch();
  const actionToPerform = useSelector(okrPanelKebabMenuActionSelector);

  useEffect(() => {
    if (isDataAvailable && actionToPerform) {
      switch (actionToPerform) {
        case ACTIONS_TO_PERFORM.DELETE:
          handleDelete();
          break;
        case ACTIONS_TO_PERFORM.REALIGN:
          handleRealign();
          break;
        case ACTIONS_TO_PERFORM.CLONE:
          handleClone();
          break;
        default:
          break;
      }
      dispatch(markActionAsDone());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionToPerform, isDataAvailable, handleDelete, handleRealign, handleClone]);
};

const useAccessControlledListOfMenuItems = ({ type }) => {
  const { objectivesName, keyResultsName } = useOrganisationSettings();
  const { canDeleteOKR, canEditOKR, canCreateKeyResult, canCreateObjective, canCloneKeyResult, canCloneObjective } =
    useOKRAccessControl();
  const { type: openSidePanelType } = useSelector(kRDrawerSecondaryPanelSelector);
  const isCreateOKRPanelOpen = openSidePanelType === SECONDARY_PANEL_TYPES.OPEN_CREATE_OKR_PANEL;
  let menuItemsToShow = [...menuItems];

  const objectionOption = {
    icon: ObjectiveIcon,
    value: OKR_TYPES.OBJECTIVE,
    label: `Add child ${objectivesName}`,
    order: 2,
  };

  const keyResultOption = {
    icon: KeyResult,
    value: OKR_TYPES.KEY_RESULT,
    label: `Add child ${keyResultsName}`,
    order: 3,
  };

  const isMsTeamsVisitor = useSelector(selectMSTeamsContext);

  if (isMsTeamsVisitor && !isMobileView()) {
    menuItemsToShow = menuItemsToShow.filter((item) => item.value !== 'SHARE');
  }

  if (!canDeleteOKR) {
    menuItemsToShow = menuItemsToShow.filter((item) => item.value !== 'DELETE');
  }
  if (!canEditOKR) {
    menuItemsToShow = menuItemsToShow.filter((item) => item.value !== 'REALIGN' && item.value !== 'MESH_AI');
  }

  if (!checkGoalGenAIEnabled()) {
    menuItemsToShow = menuItemsToShow.filter((item) => item.value !== 'MESH_AI');
  }

  if (canCreateKeyResult && !isCreateOKRPanelOpen) menuItemsToShow.unshift(keyResultOption);

  if (canCreateObjective && !isCreateOKRPanelOpen) menuItemsToShow.unshift(objectionOption);

  if (type === OKR_TYPES.OBJECTIVE) {
    menuItemsToShow = canCloneObjective ? menuItemsToShow : menuItemsToShow.filter((item) => item.value !== 'CLONE');
  } else {
    menuItemsToShow = canCloneKeyResult ? menuItemsToShow : menuItemsToShow.filter((item) => item.value !== 'CLONE');
  }

  return menuItemsToShow;
};

const StyledMenuItem = styled(MenuItem)`
  &:focus {
    background: transparent;
  }
  &:hover {
    background: #e6f1fd;
  }
`;

const SubMenuComponent = ({
  subMenuTitle,
  menuItems = [],
  isModifyTitleMenuDisabled,
  isModifyTrackingMenuDisabled,
  disabledReason,
  renderMenuItem = () => {},
  handleClick = () => {},
}) => {
  const [formattedMenuItems, setFormattedItems] = useState([]);

  useEffect(() => {
    setFormattedItems(
      menuItems.map((menuItem) => {
        const newItem = { ...menuItem };
        if (menuItem.value === 'TRACKING' && isModifyTrackingMenuDisabled) {
          newItem.isDisabled = true;
          newItem.disabledReason = disabledReason.tracking;
        }
        return newItem;
      })
    );
  }, [
    disabledReason.title,
    disabledReason.tracking,
    isModifyTitleMenuDisabled,
    isModifyTrackingMenuDisabled,
    menuItems,
  ]);

  return (
    <Accordion allowToggle boxShadow="none" padding="0px!important">
      <AccordionItem border="0px!important">
        {({ isExpanded }) => (
          <>
            <AccordionButton background="#fff" padding="0px" paddingLeft="10px" _hover={{ background: '#e6f1fd' }}>
              <Flex alignItems="center" padding="4px 0px" alignSelf="stretch" flex="1">
                <Flex justifyContent="center" alignItems="center" width="30px" height="28px">
                  <MeshLogo boxSize="16px" />
                </Flex>
                <Flex padding="4px" alignItems="flex-start" flex="1 0 0">
                  <TextBodyMedium bgGradient="linear(to-r, rgba(3, 142, 239, 1),rgba(237, 60, 101, 1))" bgClip="text">
                    {subMenuTitle}
                  </TextBodyMedium>
                </Flex>
                <Flex width="30px" height="28px" justifyContent="center" alignItems="center">
                  <ChevronDown
                    transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
                    transition="all"
                    boxSize="18px"
                    color="#666666"
                  />
                </Flex>
              </Flex>
            </AccordionButton>
            <AccordionPanel pb={0}>
              {formattedMenuItems.map((menuItem) => (
                <Tooltip isDisabled={!menuItem.isDisabled} label={menuItem.disabledReason} shouldWrapChildren>
                  <StyledMenuItem pl={8} onClick={() => handleClick(menuItem)} isDisabled={menuItem.isDisabled}>
                    {renderMenuItem(menuItem)}
                  </StyledMenuItem>
                </Tooltip>
              ))}
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Accordion>
  );
};

export const OKRKebabMenu = ({
  isModifyTitleMenuDisabled = false,
  isModifyTrackingMenuDisabled = false,
  disabledReason,
}) => {
  const dispatch = useDispatch();

  const okrDetails = useSelector(kRDrawerSelector);
  const actionToPerform = useSelector(okrPanelKebabMenuActionSelector);
  const page = useViewPageName();

  const { uuid, parentPost, timeBoundDetails, type, text, visibilityType, ifGoalChildExists, objectiveLevel } =
    okrDetails;

  const isDataAvailable = Boolean(uuid) && Boolean(text);

  const isObjective = type === OKR_TYPES.OBJECTIVE;

  const menuItems = useAccessControlledListOfMenuItems({
    type,
  }).sort((menuItem1, menuItem2) => menuItem1.order - menuItem2.order);

  /* 
    Ideally this functionality can be extracted out and redux / context can be used.
    This requirement came a bit late and doing that will touch quite a few components, so doing it this way

    TODO: extract this hook and corresponding component to common component.
    use that component in one place and these components should only trigger opening and closing.
  */
  const details = {
    parentUuid: parentPost?.uuid,
    parentText: parentPost?.text,
    parentType: parentPost?.type,
    parentVisibilityType: parentPost?.visibilityType,
    okrTimePeriod: timeBoundDetails,
    okrUuid: uuid,
    okrType: type,
    okrText: text,
    okrVisibilityType: visibilityType,
    objectiveLevel,
    okrHasNoChildren: !ifGoalChildExists,
  };

  const {
    isChangeTimelineModalOpen,
    handleCloseModal,
    handleNotRealignChildrenOKRs,
    handleRealignChildrenOKRs,
    onSelectedTimeBoundPeriodChange,
  } = useChangeTimelineRealignModalLogic({
    details,
  });

  const {
    isDeletionModalOpen,
    handleOpenDeletionModal,
    handleCloseDeletionModal,
    handleDeleteChildOKRs,
    handleRealignChildOKRs,
  } = useOKRDeletionModalLogic({
    parentUuid: parentPost?.uuid,
    parentText: parentPost?.text,
    parentType: parentPost?.type,
    parentVisibilityType: parentPost?.visibilityType,
    okrTimePeriod: timeBoundDetails,
    okrUuid: uuid,
    okrType: type,
    okrText: text,
    okrVisibilityType: visibilityType,
    objectiveLevel,
    okrHasNoChildren: !ifGoalChildExists,
  });

  const {
    isCloneModalOpen,
    openCloneModal,
    closeCloneModal,
    handleCloneAllChildren,
    handleCloneNoChildren,
    handleCloneChildObjectives,
  } = useOKRCloneModalLogic({
    okrDetails,
  });

  const { copyToClipboard } = useShareOKRLogic({ uuid, type });

  const handleRealign = useCallback(() => {
    onSelectedTimeBoundPeriodChange(timeBoundDetails);
  }, [onSelectedTimeBoundPeriodChange, timeBoundDetails]);

  const handleShare = copyToClipboard;

  const handleClone = () => {
    openCloneModal();
  };

  const handleDelete = () => {
    handleOpenDeletionModal();
  };

  const handleAddChild = (okrPanelType) => {
    const payload = {
      type: okrPanelType,
      parentPost: {
        uuid,
        type,
        text: okrDetails?.text,
        visibilityType: okrDetails?.visibilityType,
      },
      timeBoundDetails,
      parentProgressType: okrDetails?.progressType,
      parentMeasure: okrDetails?.measure,
      parentMeasureType: okrDetails?.measureType,
      onKeyResultCreation(okrDetails) {
        sendCreatedKREvent({
          eventProperties: {
            source: 'kebab Menu',
            page,
            goalId: okrDetails?.uuid,
          },
        });
      },
      onObjectiveCreation(okrDetails) {
        sendCreatedObjectiveEvent({
          eventProperties: {
            source: 'kebab Menu',
            page,
            objectiveId: okrDetails?.uuid,
            description: okrDetails?.description,
            level: okrDetails?.objectiveLevel,
            type: okrDetails?.type,
            timePeriod: okrDetails?.timeBoundDetails?.timelineTag,
          },
        });
      },
    };
    dispatch(openCreateOKRPanel(payload));
  };

  const handleAddObjective = () => {
    handleAddChild(OKR_TYPES.OBJECTIVE);
  };

  const handleAddKeyResult = () => {
    handleAddChild(OKR_TYPES.KEY_RESULT);
  };

  const handleClick = ({ value }) => {
    switch (value) {
      case OKR_TYPES.OBJECTIVE:
        handleAddObjective();
        sendClickedOnCreateObjectiveEvent({
          eventProperties: { source: 'kebab Menu', page },
        });
        sendOpenedObjectivePanelEvent({
          eventProperties: { source: 'kebab Menu', page, type: 'Edit Objective Panel' },
        });
        sendPageViewEvent({
          eventProperties: {
            page: 'Add Objective Panel',
            type: isObjective ? 'Edit Objective Panel' : 'Edit KR Panel',
            source: 'kebab Menu',
          },
        });
        break;
      case OKR_TYPES.KEY_RESULT:
        handleAddKeyResult();
        sendClickedOnCreateKRevent({
          eventProperties: { source: 'kebab Menu', page },
        });
        sendOpenedKRPanelEvent({
          eventProperties: { source: 'kebab Menu', page, type: 'Edit KR Panel' },
        });
        sendPageViewEvent({
          eventProperties: {
            page: 'Add KR Panel',
            type: isObjective ? 'Edit Objective Panel' : 'Edit KR Panel',
            source: 'kebab Menu',
          },
        });
        break;
      case 'MESH_AI':
        sendAIButtonClickedEvent({
          eventProperties: {
            source: GOALS_EVENT_SOURCES.GOALS_KEBAB_MENU,
            type: GOALS_EVENT_SOURCES.AI.type.HELP_ME_WRITE,
            goalId: okrDetails?.uuid || 'NA',
            purpose: GOALS_EVENT_SOURCES.AI.purpose.Goal_Editing,
          },
        });
        sendClickedOnHelpMeWriteEvent({
          eventProperties: {
            source: 'Edit Goals',
          },
        });
        dispatch(
          openTitleAndMetricPanel({
            isOpenedFromEditOKRKebabMenu: true,
          })
        );
        sendMeshMavenClickedEvent({
          eventProperties: {
            goalId: uuid,
            page: isObjective ? 'Edit Objective Panel' : 'Edit KR Panel',
            purpose: 'Goal Title',
            source: 'Kebab Menu',
            type: 'NO',
          },
        });
        break;
      case 'REALIGN':
        sendClickedRealignedEvent({
          eventProperties: { source: 'kebab Menu', page, type: 'Realign', goalId: okrDetails?.uuid },
        });
        handleRealign();
        break;

      case 'SHARE':
        if (okrDetails?.type === OKR_TYPES.KEY_RESULT) {
          sendShareKREvent({ eventProperties: { goalId: okrDetails?.uuid, page } });
        } else {
          sendShareObjectiveEvent({ eventProperties: { goalId: okrDetails?.uuid, page } });
        }
        handleShare();
        break;

      case 'CLONE':
        handleClone();
        break;

      case 'DELETE':
        sendClickedRealignedEvent({
          eventProperties: { source: 'kebab Menu', page, type: 'Delete', goalId: okrDetails?.uuid },
        });
        handleDelete();
        break;

      default:
        break;
    }
  };

  const renderMenuItem = ({ icon: IconComp, value, label, isDisabled }) => (
    <Flex alignItems="center" opacity={isDisabled ? '0.4' : '1'}>
      <IconComp />
      <TextBodyMedium ml={1}>{label}</TextBodyMedium>
    </Flex>
  );

  useEffect(() => {
    if (isDataAvailable && actionToPerform) {
      switch (actionToPerform) {
        case ACTIONS_TO_PERFORM.DELETE:
          handleDelete();
          break;
        case ACTIONS_TO_PERFORM.REALIGN:
          handleRealign();
          break;
        case ACTIONS_TO_PERFORM.CLONE:
          handleClone();
          break;
        default:
          break;
      }
      dispatch(markActionAsDone());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionToPerform, isDataAvailable]);

  return (
    <>
      <Menu>
        <MenuButton
          as={forwardRef((props, ref) => (
            <Button ref={ref} variant="tertiary" p={0} {...props} EndIcon={() => <Overflow boxSize="24px" />} />
          ))}
          onClick={() => {
            sendClickedOnKebabMenuEvent({
              eventProperties: {
                source: 'Goals Homepage',
                type: page || 'Home',
                cohort: page || 'Home',
              },
            });
          }}
        />
        <MenuList sx={{ width: '240px' }}>
          {menuItems.map((menuItem) =>
            menuItem.isSubMenu ? (
              <SubMenuComponent
                {...menuItem}
                isModifyTitleMenuDisabled={isModifyTitleMenuDisabled}
                isModifyTrackingMenuDisabled={isModifyTrackingMenuDisabled}
                disabledReason={disabledReason}
                renderMenuItem={renderMenuItem}
                handleClick={handleClick}
              />
            ) : (
              <StyledMenuItem key={menuItem.value} onClick={() => handleClick(menuItem)}>
                {renderMenuItem(menuItem)}
              </StyledMenuItem>
            )
          )}
        </MenuList>
      </Menu>
      <OKRDeletionModal
        isObjective={isObjective}
        isOpen={isDeletionModalOpen}
        onClose={handleCloseDeletionModal}
        okrHasNoChildren={!ifGoalChildExists}
        handleDeleteAllChildOKRs={handleDeleteChildOKRs}
        handleRealignChildOKRs={handleRealignChildOKRs}
      />
      <OKRCloneModal
        isKR={!isObjective}
        isOpen={isCloneModalOpen}
        onClose={closeCloneModal}
        okrHasNoChildren={!ifGoalChildExists}
        handleCloneAllChildren={handleCloneAllChildren}
        handleCloneNoChildren={handleCloneNoChildren}
        handleCloneChildObjectives={handleCloneChildObjectives}
      />
      <RealignModal
        isOpen={isChangeTimelineModalOpen}
        onClose={handleCloseModal}
        handleRealignChildrenOKRs={handleRealignChildrenOKRs}
        handleNotRealignChildrenOKRs={handleNotRealignChildrenOKRs}
        okrType={type}
      />
    </>
  );
};
